import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextFieldProps,
} from '@mui/material'
import { TextFieldInput } from '../../../../inputs/text-field-input'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import { ChangeEvent, JSXElementConstructor, useEffect } from 'react'
import { getErrorMessages } from '../../../../utils/error-mapping'
import { usePrevious } from 'react-use'

type BaseInputProps = Omit<TextFieldProps, 'defaultValue'> & {
  label?: string
  transform?: {
    input?: (value: string) => string
    output?: (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => string
  }
  rules?: Record<string, (value: string) => string | undefined>
}

export type AuthenticationDialogForm = {
  login: string
  password: string
}

type Props<
  TLoginProps extends BaseInputProps,
  TPasswordProps extends BaseInputProps,
> = {
  open: boolean
  onClose: () => void
  onSubmit: (data: AuthenticationDialogForm) => Promise<void>
  slots?: {
    LoginInput?: JSXElementConstructor<TLoginProps> | null
    PasswordInput?: JSXElementConstructor<TPasswordProps> | null
  }
  slotProps?: {
    loginInput?: TLoginProps
    passwordInput?: TPasswordProps
  }
  defaultValues?: {
    login?: string | null
  }
}

export const AuthenticationDialog = <
  TLoginProps extends BaseInputProps,
  TPasswordProps extends BaseInputProps,
>(
  props: Props<TLoginProps, TPasswordProps>,
) => {
  const { t } = useTranslation(['shared', 'apps'])

  const prevOpen = usePrevious(props.open)

  const formId = 'authentication-form'

  const { control, watch, reset, handleSubmit, formState } =
    useForm<AuthenticationDialogForm>({
      defaultValues: { login: '', password: '' },
    })

  const { isDirty } = formState

  const { enqueueSnackbar } = useSnackbar()

  const onSubmit = async (formData: AuthenticationDialogForm) => {
    try {
      await props.onSubmit(formData)

      enqueueSnackbar({
        message: t('apps:authentication.success_message'),
        variant: 'success',
      })
      props.onClose()
    } catch (error) {
      const errorMessage = getErrorMessages(error)
      enqueueSnackbar({ message: errorMessage, variant: 'error' })
    }
  }

  useEffect(() => {
    if (props.open !== prevOpen && props.defaultValues) {
      reset(
        { login: props.defaultValues.login ?? '', password: '' },
        { keepDefaultValues: false },
      )
    }
  }, [prevOpen, props.defaultValues, props.open, reset])

  const LoginInput = props.slots?.LoginInput ?? TextFieldInput
  const PasswordInput = props.slots?.PasswordInput ?? TextFieldInput
  const loginProps = props.slotProps?.loginInput as TLoginProps
  const passwordProps = props.slotProps?.passwordInput as TPasswordProps

  return (
    <Dialog open={props.open} maxWidth="xs" fullWidth>
      <DialogTitle>{t('apps:authentication.title')}</DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <form id={formId} noValidate onSubmit={handleSubmit(onSubmit)}>
          <Stack gap={2}>
            <LoginInput
              autoComplete="off"
              {...loginProps}
              rules={{
                validate: {
                  ...loginProps.rules,
                  required: (value) => {
                    if (!value && watch('password')) {
                      return t('apps:authentication.required_fields', {
                        login:
                          loginProps.label ?? t('apps:authentication.login'),
                        password:
                          passwordProps.label ??
                          t('apps:authentication.password'),
                      })
                    }
                  },
                },
              }}
              name="login"
              control={control}
            />

            <PasswordInput
              type="text"
              autoComplete="off"
              {...passwordProps}
              rules={{
                validate: {
                  ...passwordProps.rules,
                  required: (value) => {
                    if (!value && watch('login')) {
                      return t('apps:authentication.required_fields', {
                        login:
                          loginProps.label ?? t('apps:authentication.login'),
                        password:
                          passwordProps.label ??
                          t('apps:authentication.password'),
                      })
                    }
                  },
                },
              }}
              name="password"
              control={control}
            />
          </Stack>
        </form>
      </DialogContent>

      <DialogActions>
        <Button color="secondary" size="small" onClick={props.onClose}>
          {t('shared:action.cancel')}
        </Button>

        <Button
          color="primary"
          size="small"
          form={formId}
          disabled={!isDirty}
          type="submit"
        >
          {t('shared:action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
